import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { TextField, Checkbox } from '@anyemp/ui-kit';
import LinksEditor from './LinksEditor';
import ConfirmDialog from '../UI/ConfirmDialog';

const emptyStep = () => ({
  title: '',
  description: '',
  offset_days: 0,
  links: [],
  notify_before_days: 0,
  is_required: true,
});

const StepRow = ({ step, index, total, canEdit, onSave, onDelete, onMoveUp, onMoveDown }) => {
  const isNew = !step.id;
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState({ ...step });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!draft.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(draft, isNew);
      setEditing(false);
    } catch {
      // toast handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onDelete(); // discards the unsaved new step
    } else {
      setDraft({ ...step });
      setEditing(false);
    }
  };

  return (
    <div className="border border-light-border-primary dark:border-dark-border-primary rounded-md p-3 bg-light-bg-secondary dark:bg-dark-bg-secondary">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 text-light-text-tertiary dark:text-dark-text-tertiary">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canEdit || index === 0 || editing}
            className="flex h-10 w-10 items-center justify-center rounded hover:bg-light-bg-tertiary hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-dark-bg-tertiary"
            title="Move up"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <GripVertical className="mx-auto h-3.5 w-3.5 opacity-50" />
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canEdit || index === total - 1 || editing}
            className="flex h-10 w-10 items-center justify-center rounded hover:bg-light-bg-tertiary hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-dark-bg-tertiary"
            title="Move down"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-3">
              <div>
                <TextField
                  label="Title"
                  required
                  size="small"
                  fullWidth
                  type="text"
                  placeholder="e.g. Welcome meeting, HR documents"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  helperText="Shown on the talent's profile checklist and in the calendar."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <TextField
                    label="Offset days"
                    size="small"
                    fullWidth
                    type="number"
                    value={draft.offset_days}
                    onChange={(e) => setDraft({ ...draft, offset_days: parseInt(e.target.value, 10) || 0 })}
                    helperText="Days from the anchor date (set on the playbook). 0 = same day as anchor, 7 = a week later, -1 = day before. Determines the step's due date."
                  />
                </div>
                <div>
                  <TextField
                    label="Notify before"
                    size="small"
                    fullWidth
                    type="number"
                    min={0}
                    value={draft.notify_before_days}
                    onChange={(e) => setDraft({ ...draft, notify_before_days: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    helperText="Days before the due date to send a reminder to the talent's manager. 0 = on the due date itself."
                  />
                </div>
              </div>

              <div>
                <TextField
                  label="Description"
                  multiline
                  rows={2}
                  size="small"
                  fullWidth
                  placeholder="What should be done at this step?"
                  value={draft.description || ''}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  helperText="Optional. Shown under the step title on the talent profile."
                />
              </div>

              <div>
                <Checkbox
                  label="Required"
                  color="primary"
                  size="small"
                  checked={draft.is_required !== false}
                  onChange={(checked) => setDraft({ ...draft, is_required: checked })}
                />
                <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5 ml-5">
                  The instance auto-completes only when all required steps are checked off. Optional steps don't block completion.
                </p>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wide text-light-text-tertiary mb-1">Links</div>
                <LinksEditor value={draft.links} onChange={(links) => setDraft({ ...draft, links })} />
                <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
                  Links to documents/forms (Notion, Confluence, Google Forms). Appear as clickable chips next to the step in the talent's profile and in the calendar sidebar.
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="min-h-10 w-full px-3 py-2 text-xs border border-light-border-primary dark:border-dark-border-primary rounded text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary sm:w-auto"
                >
                  <X className="inline h-3 w-3 mr-0.5" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="min-h-10 w-full px-3 py-2 text-xs bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded disabled:opacity-50 sm:w-auto"
                >
                  <Save className="inline h-3 w-3 mr-0.5" /> {saving ? 'Saving...' : isNew ? 'Add' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="min-w-0 break-words text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  {step.title}
                </span>
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                  Day {step.offset_days >= 0 ? '+' : ''}{step.offset_days}
                </span>
                {step.notify_before_days > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    notify -{step.notify_before_days}d
                  </span>
                )}
                {step.is_required === false && (
                  <span className="text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary">(optional)</span>
                )}
                {(step.links || []).length > 0 && (
                  <span className="text-[10px] text-light-text-tertiary dark:text-dark-text-tertiary">
                    · {step.links.length} link{step.links.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {step.description && (
                <p className="mt-0.5 break-words text-xs text-light-text-secondary dark:text-dark-text-secondary">{step.description}</p>
              )}
            </div>
          )}
        </div>

        {!editing && canEdit && (
          <div className="flex shrink-0 items-start gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="min-h-10 px-3 py-2 text-xs text-primary-600 dark:text-primary-400 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary rounded"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary hover:bg-light-bg-tertiary hover:text-red-600 dark:hover:bg-dark-bg-tertiary dark:hover:text-red-400"
              title="Delete step"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => { setConfirmDelete(false); onDelete(); }}
        title={`Delete step "${step.title}"?`}
        message="This won't affect already-started instances (they keep their snapshot)."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

const StepsEditor = ({ steps = [], canEdit, onAdd, onUpdate, onDelete, onReorder }) => {
  const [adding, setAdding] = useState(false);

  const handleSaveNew = async (draft) => {
    await onAdd(draft);
    setAdding(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
            Steps ({steps.length})
          </h3>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">
            Each step becomes a checklist item on the talent's profile and a calendar event with its own due-date. Reorder with ↑/↓.
          </p>
        </div>
        {canEdit && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex min-h-10 flex-shrink-0 items-center gap-1 self-end px-3 py-2 text-xs bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded"
          >
            <Plus className="h-3 w-3" /> Add step
          </button>
        )}
      </div>

      {steps.length === 0 && !adding ? (
        <div className="text-center py-6 text-light-text-tertiary dark:text-dark-text-tertiary text-xs">
          {canEdit
            ? 'No steps yet. Click "Add step" to create the first one.'
            : 'No steps yet.'}
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((step, i) => (
            <StepRow
              key={step.id}
              step={step}
              index={i}
              total={steps.length}
              canEdit={canEdit}
              onSave={(draft) => onUpdate(step.id, draft)}
              onDelete={() => onDelete(step.id)}
              onMoveUp={() => onReorder(i, i - 1)}
              onMoveDown={() => onReorder(i, i + 1)}
            />
          ))}
          {adding && (
            <StepRow
              step={emptyStep()}
              index={steps.length}
              total={steps.length + 1}
              canEdit
              onSave={handleSaveNew}
              onDelete={() => setAdding(false)}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StepsEditor;
